import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Panel, inputCls } from "@/components/cms/kit";
import {
  ShieldCheck, AlertTriangle, Loader2, Gauge, FileSearch, CheckCircle2, XCircle, Sparkles,
} from "lucide-react";

/* Shared, self-contained provider/model picker (no auto LLM calls). */
export function useProviderModel() {
  const [providers, setProviders] = useState([]);
  const [catalog, setCatalog] = useState({});
  const [pid, setPid] = useState("");
  const [model, setModel] = useState("");
  useEffect(() => {
    api.llmProviders().then((d) => { const l = d.providers || []; setProviders(l); const p = l.find((x) => x.configured && x.enabled) || l[0]; if (p) setPid(p.id); }).catch(() => {});
    api.llmCatalog().then((d) => setCatalog(d.catalog || {})).catch(() => {});
  }, []);
  const models = useMemo(() => (catalog[pid] || []).filter((m) => m.status !== "deprecated"), [catalog, pid]);
  useEffect(() => { const r = models.find((m) => m.recommended) || models[0]; setModel(r ? r.id : ""); }, [pid, catalog]); // eslint-disable-line
  const active = providers.find((p) => p.id === pid);
  const ready = !!(active && active.configured && active.enabled && model);
  return { providers, models, pid, setPid, model, setModel, ready };
}

const READINESS_STYLE = {
  READY: "bg-emerald-400/15 text-emerald-300",
  NEEDS_MORE_RESEARCH: "bg-amber-400/15 text-amber-300",
  INSUFFICIENT_SOURCE: "bg-klyroo-red/15 text-klyroo-red",
  REJECT: "bg-klyroo-red/20 text-klyroo-red",
};
const READINESS_LABEL = {
  READY: "Ready", NEEDS_MORE_RESEARCH: "Needs more research",
  INSUFFICIENT_SOURCE: "Insufficient source", REJECT: "Reject",
};
const PASS_STYLE = {
  PASS: "bg-emerald-400/15 text-emerald-300",
  PASS_WITH_WARNINGS: "bg-lime-400/15 text-lime-300",
  NEEDS_REVIEW: "bg-amber-400/15 text-amber-300",
  FAIL: "bg-klyroo-red/15 text-klyroo-red",
};

export function ReadinessBadge({ status }) {
  if (!status) return null;
  return <span className={`text-[10px] font-mono px-2 py-0.5 rounded ${READINESS_STYLE[status] || "bg-white/10 text-slate-300"}`} data-testid="readiness-badge">{READINESS_LABEL[status] || status}</span>;
}

function Score({ label, value, tid }) {
  const v = value == null ? null : Number(value);
  const col = v == null ? "text-slate-400" : v >= 75 ? "text-emerald-300" : v >= 50 ? "text-amber-300" : "text-klyroo-red";
  return (
    <div className="rounded-lg bg-white/5 border border-white/10 px-2.5 py-1.5" data-testid={tid}>
      <div className="text-[9px] font-mono uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`text-sm font-bold ${col}`}>{v == null ? "—" : `${v}`}<span className="text-[10px] text-slate-500">/100</span></div>
    </div>
  );
}

function List({ title, items, tone = "slate", tid }) {
  if (!items || items.length === 0) return null;
  const col = tone === "red" ? "text-klyroo-red" : tone === "amber" ? "text-amber-300" : "text-slate-300";
  return (
    <div data-testid={tid}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1">{title}</div>
      <ul className="list-disc pl-4 space-y-0.5">{items.map((x, i) => <li key={i} className={`text-xs ${col}`}>{typeof x === "string" ? x : JSON.stringify(x)}</li>)}</ul>
    </div>
  );
}

/* Editor panel: read-only readiness (from the brief) + explicit Run Quality Check.
   NEVER calls the LLM on mount — the admin must click Run Quality Check. */
export function ContentQualityPanel({ f }) {
  const { providers, models, pid, setPid, model, setModel, ready } = useProviderModel();
  const [readiness, setReadiness] = useState(null);
  const [quality, setQuality] = useState(f.quality_check || null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [regenning, setRegenning] = useState(false);
  const [regenMsg, setRegenMsg] = useState(null);

  const regenDepth = async () => {
    setRegenning(true); setRegenMsg(null);
    try {
      const r = await api.regenerateDepth(f.slug, { provider: pid, model });
      if (r.success) { setRegenMsg(`Regenerated — ${r.length_check?.status}, ${r.analytics?.actual_words} words. Reload the article to see changes.`); toast.success("Regenerated at target depth"); }
      else { setRegenMsg(r.reason || "Regeneration unavailable"); toast.info(r.reason || "Regeneration unavailable"); }
    } catch (e) { setRegenMsg(e.response?.data?.detail || "Regeneration failed"); }
    finally { setRegenning(false); }
  };

  useEffect(() => {
    if (f.research_item_id) api.researchArticleContext(f.research_item_id).then((d) => setReadiness(d.context?.readiness || null)).catch(() => {});
  }, [f.research_item_id]);

  const run = async () => {
    if (!f.slug) { toast.error("Save the article first"); return; }
    setBusy(true); setErr(null);
    try {
      const r = await api.articleQualityCheck(f.slug, { provider: pid, model });
      if (r.success) { setQuality(r.quality); toast.success("Quality check complete"); }
      else setErr(r.error || { code: "ERROR", message: "Failed" });
    } catch (e) { setErr({ code: "REQUEST_FAILED", message: e.response?.data?.detail || "Request failed" }); }
    finally { setBusy(false); }
  };

  return (
    <Panel title="Content Quality" testid="content-quality-panel">
      <div className="flex items-start gap-2 -mt-1">
        <ShieldCheck size={15} className="text-klyroo-cyan mt-0.5 shrink-0" />
        <p className="text-xs text-slate-500">Evidence readiness &amp; article fidelity. Nothing runs automatically — the quality check runs only when you click it, and it never edits or publishes the article.</p>
      </div>

      {readiness && (
        <div className="rounded-xl border border-white/10 bg-white/5 p-3 space-y-2" data-testid="readiness-block">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400 inline-flex items-center gap-1"><Gauge size={12} /> Readiness</span>
            <ReadinessBadge status={readiness.readiness_status} />
            <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-white/5 text-slate-300" data-testid="readiness-word-range">rec. words: {readiness.recommended_word_range}</span>
            {readiness.assessment_mode && <span className="text-[10px] font-mono text-slate-500">({readiness.assessment_mode})</span>}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Score label="Evidence" value={readiness.evidence_coverage_score} tid="readiness-evidence" />
            <Score label="Source" value={readiness.source_coverage_score} tid="readiness-source" />
            <Score label="Factual" value={readiness.factual_completeness_score} tid="readiness-factual" />
            <Score label="Depth" value={readiness.article_depth_score} tid="readiness-depth" />
          </div>
          {readiness.readiness_reason && <p className="text-xs text-slate-400" data-testid="readiness-reason">{readiness.readiness_reason}</p>}
          <List title="Missing critical facts" items={readiness.missing_critical_facts} tone="amber" tid="readiness-missing" />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <select value={pid} onChange={(e) => setPid(e.target.value)} data-testid="quality-provider" className={`${inputCls} !w-auto text-xs`}>
          {providers.map((p) => <option key={p.id} value={p.id}>{p.name}{p.configured ? (p.enabled ? "" : " (disabled)") : " (not configured)"}</option>)}
        </select>
        <select value={model} onChange={(e) => setModel(e.target.value)} data-testid="quality-model" className={`${inputCls} !w-auto text-xs`}>
          {models.map((m) => <option key={m.id} value={m.id}>{m.display_name}</option>)}
        </select>
        <button type="button" onClick={run} disabled={!ready || busy || !f.slug} data-testid="run-quality-check-btn"
          className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-klyroo-cyan text-black text-sm font-semibold hover:bg-white disabled:opacity-50">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <FileSearch size={14} />} Run Quality Check
        </button>
      </div>
      {!f.slug && <p className="text-xs text-amber-300" data-testid="quality-save-first">Save the article before running a quality check.</p>}
      {!ready && f.slug && <p className="text-xs text-amber-300" data-testid="quality-not-ready">Select a configured, enabled provider in the AI / LLM tab to run the check.</p>}
      {err && <div className="text-sm" data-testid="quality-error"><span className="text-klyroo-red font-mono">✕ {err.code}</span> <span className="text-slate-400">{err.message}</span></div>}

      {quality && (
        <div className="rounded-xl border border-white/10 bg-klyroo-bg/50 p-3 space-y-3" data-testid="quality-result">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`text-xs font-mono px-2.5 py-1 rounded inline-flex items-center gap-1 ${PASS_STYLE[quality.pass_status] || "bg-white/10 text-slate-300"}`} data-testid="quality-pass-status">
              {quality.pass_status === "PASS" ? <CheckCircle2 size={13} /> : quality.pass_status === "FAIL" ? <XCircle size={13} /> : <AlertTriangle size={13} />} {quality.pass_status}
            </span>
            {quality.word_count != null && <span className="text-[10px] font-mono text-slate-500">{quality.body_word_count != null ? quality.body_word_count : quality.word_count} body words</span>}
          </div>
          {quality.length_check && (
            <div className={`rounded-lg px-3 py-2 text-xs border ${quality.length_check.status === "OK" ? "bg-emerald-400/10 border-emerald-400/30 text-emerald-200" : "bg-klyroo-red/10 border-klyroo-red/30 text-klyroo-red"}`} data-testid="quality-length-check">
              <span className="font-mono font-semibold">{quality.length_check.status}</span> · {quality.length_check.message}
              {quality.length_check.status === "UNDER_TARGET" && (
                <button onClick={regenDepth} disabled={regenning || !ready || !f.slug} data-testid="regenerate-depth-btn" className="mt-1.5 block inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-klyroo-cyan text-black font-semibold hover:bg-white disabled:opacity-50">{regenning ? <Loader2 size={12} className="animate-spin" /> : <FileSearch size={12} />} Regenerate at Target Depth</button>
              )}
              {regenMsg && <div className="mt-1 text-slate-300" data-testid="regen-msg">{regenMsg}</div>}
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <Score label="Overall" value={quality.overall_score} tid="quality-overall" />
            <Score label="Fidelity" value={quality.factual_fidelity_score} tid="quality-fidelity" />
            <Score label="Evidence" value={quality.evidence_coverage_score} tid="quality-evidence" />
            <Score label="Specificity" value={quality.specificity_score} tid="quality-specificity" />
            <Score label="Usefulness" value={quality.usefulness_score} tid="quality-usefulness" />
            <Score label="SEO" value={quality.seo_score} tid="quality-seo" />
          </div>
          <List title="Critical issues" items={quality.critical_issues} tone="red" tid="quality-critical" />
          <List title="Unsupported claims" items={quality.unsupported_claims} tone="amber" tid="quality-unsupported" />
          <List title="Generic filler" items={quality.generic_filler_sections} tone="amber" tid="quality-filler" />
          <List title="Warnings" items={quality.warnings} tone="amber" tid="quality-warnings" />
          <List title="Recommended fixes" items={quality.recommended_fixes} tid="quality-fixes" />
          <p className="text-[10px] font-mono text-slate-500 flex items-center gap-1"><Sparkles size={10} /> Reported findings only — this never edits or publishes the article.</p>
        </div>
      )}
    </Panel>
  );
}
